class TestimonialTabs extends HTMLElement {
  constructor() {
    super();
    this.tabTriggers = this.querySelectorAll('.testimonial-tabs-trigger');
    this.tabContents = this.querySelectorAll('.testimonial-content');
    this.autoplay = this.getAttribute('data-autoplay') === 'true';
    this.autoplaySpeed = parseInt(this.getAttribute('data-autoplay-speed') || 5000);
    this.currentIndex = 0;
    this.autoplayInterval = null;
    
    this.bindEvents();
    this.init();
  }

  init() {
    // Set the first tab as active
    if (this.tabTriggers.length > 0) {
      this.setActiveTab(0);
    }
    
    // Start autoplay if enabled
    if (this.autoplay) {
      this.startAutoplay();
    }
  }

  bindEvents() {
    // Click events for tab triggers
    this.tabTriggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => {
        this.setActiveTab(index);
        if (this.autoplay) {
          this.resetAutoplay();
        }
      });
      
      // Keyboard accessibility
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.setActiveTab(index);
          if (this.autoplay) {
            this.resetAutoplay();
          }
        }
      });
    });

    // Handle keyboard navigation
    this.addEventListener('keydown', this.handleKeyNavigation.bind(this));
    
    // Pause autoplay on hover or focus
    if (this.autoplay) {
      this.addEventListener('mouseenter', this.pauseAutoplay.bind(this));
      this.addEventListener('mouseleave', this.startAutoplay.bind(this));
      this.addEventListener('focusin', this.pauseAutoplay.bind(this));
      this.addEventListener('focusout', () => {
        // Only restart if the focus is completely outside the tabs
        if (!this.contains(document.activeElement)) {
          this.startAutoplay();
        }
      });
    }
  }

  setActiveTab(index) {
    this.currentIndex = index;
    
    // Update tab triggers
    this.tabTriggers.forEach((trigger, i) => {
      const isActive = i === index;
      trigger.classList.toggle('active', isActive);
      trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    // Update tab contents
    this.tabContents.forEach((content, i) => {
      content.classList.toggle('active', i === index);
    });
    
    // Ensure the active tab is visible (scroll into view on mobile)
    if (window.innerWidth < 750) {
      this.tabTriggers[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  handleKeyNavigation(event) {
    // Only handle arrow keys if a tab trigger is focused
    if (!event.target.classList.contains('testimonial-tabs-trigger')) return;
    
    const currentIndex = Array.from(this.tabTriggers).indexOf(event.target);
    let newIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        newIndex = (currentIndex + 1) % this.tabTriggers.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        newIndex = (currentIndex - 1 + this.tabTriggers.length) % this.tabTriggers.length;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = this.tabTriggers.length - 1;
        break;
      default:
        return;
    }

    this.tabTriggers[newIndex].focus();
    this.setActiveTab(newIndex);
    
    if (this.autoplay) {
      this.resetAutoplay();
    }
  }
  
  startAutoplay() {
    if (!this.autoplay || this.tabTriggers.length <= 1) return;
    
    this.pauseAutoplay(); // Clear any existing interval
    
    this.autoplayInterval = setInterval(() => {
      const nextIndex = (this.currentIndex + 1) % this.tabTriggers.length;
      this.setActiveTab(nextIndex);
    }, this.autoplaySpeed);
  }
  
  pauseAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }
  
  resetAutoplay() {
    this.pauseAutoplay();
    this.startAutoplay();
  }
  
  disconnectedCallback() {
    // Clean up autoplay when the component is removed
    this.pauseAutoplay();
  }
}

customElements.define('testimonial-tabs', TestimonialTabs);

// Initialize for Shopify editor
document.addEventListener('shopify:section:load', (event) => {
  const testimonialTabs = event.target.querySelector('testimonial-tabs');
  if (testimonialTabs) {
    // Reconnect the component
    testimonialTabs.tabTriggers = testimonialTabs.querySelectorAll('.testimonial-tabs-trigger');
    testimonialTabs.tabContents = testimonialTabs.querySelectorAll('.testimonial-content');
    testimonialTabs.init();
    testimonialTabs.bindEvents();
  }
});

document.addEventListener('shopify:section:select', (event) => {
  const testimonialTabs = event.target.querySelector('testimonial-tabs');
  if (testimonialTabs && testimonialTabs.autoplay) {
    testimonialTabs.pauseAutoplay();
  }
});

document.addEventListener('shopify:section:deselect', (event) => {
  const testimonialTabs = event.target.querySelector('testimonial-tabs');
  if (testimonialTabs && testimonialTabs.autoplay) {
    testimonialTabs.startAutoplay();
  }
});

// Handle block selection in the Shopify theme editor
document.addEventListener('shopify:block:select', (event) => {
  const blockId = event.target.id;
  const testimonialSection = event.target.closest('.testimonial-section');
  if (!testimonialSection) return;
  
  const testimonialTabs = testimonialSection.querySelector('testimonial-tabs');
  if (!testimonialTabs) return;
  
  // Find the tab trigger associated with the selected block
  const contentId = blockId.replace('tab-content-', '');
  const tabTriggers = testimonialTabs.querySelectorAll('.testimonial-tabs-trigger');
  
  tabTriggers.forEach((trigger, index) => {
    if (trigger.dataset.target === blockId || trigger.getAttribute('aria-controls') === blockId) {
      testimonialTabs.setActiveTab(index);
      
      // Pause autoplay while in editor
      if (testimonialTabs.autoplay) {
        testimonialTabs.pauseAutoplay();
      }
    }
  });
});

document.addEventListener('shopify:block:deselect', (event) => {
  const testimonialSection = event.target.closest('.testimonial-section');
  if (!testimonialSection) return;
  
  const testimonialTabs = testimonialSection.querySelector('testimonial-tabs');
  if (!testimonialTabs || !testimonialTabs.autoplay) return;
  
  testimonialTabs.startAutoplay();
});